/** Folder detail page: shows lessons in a specific folder with rename/color/delete. */
import { useState, useDeferredValue } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, MoreHorizontal, Pencil, Palette, Trash2 } from 'lucide-react';
import { Button as AriaButton } from 'react-aria-components';
import { toast } from 'sonner';
import {
  SearchField,
  Button,
  PageContainer,
  Dialog,
  TextField,
  ColorPalettePicker,
  MenuTrigger,
  Menu,
  MenuItem,
  MenuSeparator,
} from '@/interfaces/learner/ui';
import { LessonCard } from './components/LessonCard';
import {
  useFolders,
  usePersonalLessons,
  useRenameFolder,
  useDeleteFolder,
} from '@/shared/api/hooks';
import { PAGE_SIZE, slugify } from './utils';

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function FolderPage() {
  const { folderSlug } = useParams<{ folderSlug: string }>();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const deferredSearch = useDeferredValue(search);
  const isSearching = deferredSearch.trim().length > 0;

  const [showRename, setShowRename] = useState(false);
  const [showColor, setShowColor] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [colorValue, setColorValue] = useState<string | undefined>(undefined);

  const renameFolder = useRenameFolder();
  const deleteFolder = useDeleteFolder();

  const { data: folders = [] } = useFolders();
  const folder = folders.find((f) => slugify(f.name) === folderSlug);

  const { data: lessonsData } = usePersonalLessons(
    folder
      ? {
          folderId: folder.id,
          search: isSearching ? deferredSearch.trim() : undefined,
          page,
          pageSize: PAGE_SIZE,
        }
      : undefined
  );

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const handleRenameSubmit = () => {
    if (!folder) return;
    const name = renameValue.trim();
    if (!name || name === folder.name) {
      setShowRename(false);
      return;
    }
    renameFolder.mutate(
      { folderId: folder.id, name, color: folder.color },
      {
        onSuccess: () => {
          setShowRename(false);
          navigate(`/lessons/${slugify(name)}`, { replace: true });
        },
        onError: (err) => toast.error(err.message || 'Failed to rename folder'),
      }
    );
  };

  const handleColorSubmit = () => {
    if (!folder) return;
    renameFolder.mutate(
      { folderId: folder.id, name: folder.name, color: colorValue ?? null },
      {
        onSuccess: () => setShowColor(false),
        onError: (err) => toast.error(err.message || 'Failed to update color'),
      }
    );
  };

  const handleDelete = () => {
    if (!folder) return;
    deleteFolder.mutate(folder.id, {
      onSuccess: () => {
        setShowDelete(false);
        navigate('/lessons');
      },
      onError: (err) => toast.error(err.message || 'Failed to delete folder'),
    });
  };

  const lessons = lessonsData?.data ?? [];
  const pagination = lessonsData?.pagination;
  const accentColor = folder?.color ?? undefined;

  // ─── Folder not found ──────────────────────────────────────────────────────

  if (folders.length > 0 && !folder) {
    return (
      <PageContainer
        style={{
          paddingTop: 'var(--page-top-spacing)',
          paddingBottom: 'var(--space-16)',
        }}
      >
        <Button
          variant="ghost"
          size="sm"
          icon={<ArrowLeft size={14} strokeWidth={2} />}
          onPress={() => navigate('/lessons')}
          style={{ marginBottom: 24 }}
        >
          My Lessons
        </Button>
        <p
          className="text-body-muted"
          style={{
            margin: 0,
          }}
        >
          Folder not found
        </p>
      </PageContainer>
    );
  }

  // ─── Loading (folders haven't loaded yet) ─────────────────────────────────

  if (!folder) {
    return null;
  }

  // ─── Folder view ──────────────────────────────────────────────────────────

  return (
    <>
      <PageContainer
        style={{
          paddingTop: 'var(--page-top-spacing)',
          paddingBottom: 'var(--space-16)',
        }}
      >
        {/* Back link */}
        <Button
          variant="ghost"
          size="sm"
          icon={<ArrowLeft size={14} strokeWidth={2} />}
          onPress={() => navigate('/lessons')}
          style={{ marginBottom: 24 }}
        >
          My Lessons
        </Button>

        {/* Title + three-dot menu */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            marginBottom: 20,
          }}
        >
          <h1
            className="text-section-title"
            style={{
              margin: 0,
            }}
          >
            {folder.name}
          </h1>

          <MenuTrigger>
            <AriaButton
              aria-label="Folder options"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 32,
                height: 32,
                borderRadius: 'var(--control-radius-sm)',
                background: 'transparent',
                border: 'none',
                color: 'var(--color-text-muted)',
                cursor: 'pointer',
                transition: 'background 0.15s',
                flexShrink: 0,
              }}
              className="hover:bg-[var(--color-surface-muted)]"
            >
              <MoreHorizontal size={16} strokeWidth={2} />
            </AriaButton>
            <Menu>
              <MenuItem
                icon={<Pencil size={14} strokeWidth={1.75} />}
                onAction={() => {
                  setRenameValue(folder.name);
                  setShowRename(true);
                }}
              >
                Rename
              </MenuItem>
              <MenuItem
                icon={<Palette size={14} strokeWidth={1.75} />}
                onAction={() => {
                  setColorValue(folder.color ?? undefined);
                  setShowColor(true);
                }}
              >
                Change color
              </MenuItem>
              <MenuSeparator />
              <MenuItem
                variant="danger"
                icon={<Trash2 size={14} strokeWidth={1.75} />}
                onAction={() => setShowDelete(true)}
              >
                Delete
              </MenuItem>
            </Menu>
          </MenuTrigger>
        </div>

        {/* Search */}
        <SearchField
          value={search}
          onChange={handleSearchChange}
          placeholder="Search in folder..."
          size="md"
          style={{ marginBottom: 28 }}
        />

        {/* Lessons grid */}
        {lessons.length === 0 ? (
          <p
            className="text-body-muted"
            style={{
              margin: 0,
            }}
          >
            {isSearching ? 'No lessons found' : 'This folder is empty'}
          </p>
        ) : (
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                gap: 12,
              }}
            >
              {lessons.map((lesson) => (
                <LessonCard key={lesson.id} lesson={lesson} accentColor={accentColor} />
              ))}
            </div>

            {pagination && pagination.page < pagination.totalPages && (
              <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
                <Button variant="ghost" size="sm" onPress={() => setPage((p) => p + 1)}>
                  Show more
                </Button>
              </div>
            )}
          </>
        )}
      </PageContainer>

      {/* Rename dialog */}
      <Dialog
        isOpen={showRename}
        onClose={() => setShowRename(false)}
        title="Rename folder"
        size="sm"
      >
        <TextField
          label="Folder name"
          value={renameValue}
          onChange={setRenameValue}
          size="md"
          autoFocus
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <Button variant="ghost" size="sm" onPress={() => setShowRename(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            isDisabled={renameFolder.isPending}
            onPress={handleRenameSubmit}
          >
            Rename
          </Button>
        </div>
      </Dialog>

      {/* Change color dialog */}
      <Dialog
        isOpen={showColor}
        onClose={() => setShowColor(false)}
        title="Change folder color"
        size="sm"
      >
        <ColorPalettePicker value={colorValue} onChange={setColorValue} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <Button variant="ghost" size="sm" onPress={() => setShowColor(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            isDisabled={renameFolder.isPending}
            onPress={handleColorSubmit}
          >
            Save
          </Button>
        </div>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog
        isOpen={showDelete}
        onClose={() => setShowDelete(false)}
        title="Delete folder?"
        size="sm"
      >
        <p
          className="text-body"
          style={{
            margin: 0,
          }}
        >
          Lessons in this folder will be moved to Unfiled.
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <Button variant="ghost" size="sm" onPress={() => setShowDelete(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            size="sm"
            isDisabled={deleteFolder.isPending}
            onPress={handleDelete}
          >
            Delete
          </Button>
        </div>
      </Dialog>
    </>
  );
}
